
import React from 'react';
import { UserProfile, Client } from '@/shared/types';

interface TemplateProps {
  profile: UserProfile;
  client?: Client | null;
  title: string;
  metaData: { label: string; value: string }[];
  children: React.ReactNode;
  footerNote?: string;
  type: string;
  containerClass: string;
  LogoImage: React.FC<{ size: 'sm' | 'md' | 'lg' | 'xl'; className?: string; light?: boolean }>;
  hideHeader?: boolean;
}

const ClassicTemplate: React.FC<TemplateProps> = ({
  profile, client, title, metaData, children, footerNote, type, containerClass, LogoImage, hideHeader = false
}) => {
  return (
    <div className={containerClass}>
      {/* Top Accent */}
      <div style={{ backgroundColor: profile.brandColor }} className="h-3 w-full"></div>

      <div className="px-16 py-12 flex-1 flex flex-col">
        {!hideHeader && (
          <>
            {/* Header */}
            <div className="flex flex-col items-center text-center border-b border-neutral-100 pb-10 mb-10">
              <LogoImage size="lg" className="mb-6" />
              <h1 className="text-3xl font-bold text-neutral-900 tracking-tight mb-2">{profile.name}</h1>
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-neutral-500 font-medium">
                 <span>{profile.email}</span>
                 {profile.phone && <span>• {profile.phone}</span>}
                 {profile.address && <span>• {profile.address}</span>}
              </div>
            </div>

            {/* Document Title & Meta */}
            <div className="flex justify-between items-end mb-12">
              <div>
                 <h2 className="text-xs font-bold text-neutral-400 uppercase tracking-widest mb-2">Prepared For</h2>
                 {client ? (
                   <div className="text-neutral-800">
                    <p className="font-bold text-xl leading-tight">{client.name}</p>
                    {client.company && <p className="text-base opacity-80 mt-1">{client.company}</p>}
                    <p className="text-sm text-neutral-500 mt-2">{client.address}</p>
                   </div>
                 ) : (
                   <p className="text-neutral-400 italic">No Client Selected</p>
                 )}
              </div>
              <div className="text-right">
                 <h2 className="text-5xl font-light text-neutral-200 uppercase mb-2 tracking-tighter">{type}</h2>
                 <div className="space-y-1">
                   <p className="font-bold text-lg text-neutral-900" style={{ color: profile.brandColor }}>{title}</p>
                   <div className="flex flex-col items-end text-sm pt-2">
                    {metaData.map((m, i) => (
                      <div key={i} className="flex gap-4">
                        <span className="text-neutral-400 font-medium">{m.label}:</span>
                        <span className="font-bold text-neutral-700">{m.value}</span>
                      </div>
                    ))}
                   </div>
                 </div>
              </div>
            </div>
          </>
        )}

        {/* Content Area */}
        <div className="flex-1">
          {children}
        </div>

        {/* Footer */}
        <div className="mt-auto pt-8 border-t border-neutral-100 text-center">
          <p className="text-neutral-600 font-medium italic mb-2">"{footerNote || 'Thank you for your business.'}"</p>
          <p className="text-xs text-neutral-400 uppercase tracking-wider">Generated by SwaySuite</p>
        </div>
      </div>
    </div>
  );
};

export default ClassicTemplate;
